import { attachedFileFragment } from '@reconcept-platform/shared/data-access';
import { gql } from 'apollo-angular';
import { workspaceUserItemFragment } from '../user/user.fragments';
import { workspaceViewerCanFragment } from '../workspace/workspace.fragments';
import { goalCardItemForExportFragment, goalCardItemFragment } from './goal-card.fragments';

export const goalCardWithCommentsQuery = gql`
  query GoalCardWithComments($id: ID!) {
    node(id: $id) {
      ... on GoalCard {
        ...GoalCardItemFragment

        cardComments {
          nodes {
            id
            attachedFiles {
              ...AttachedFile
            }
          }
        }
      }
    }
  }
  ${goalCardItemFragment}
  ${attachedFileFragment}
`;

export const GoalCardItemQuery = gql`
  query GoalCardItemQuery($id: ID!) {
    node(id: $id) {
      ... on GoalCard {
        ...GoalCardItemFragment

        workspace {
          id
          title
          dashboard {
            id
            dashboardFormat {
              id
              shortName
            }
          }
          ...WorkspaceViewerCanFragment
        }

        users {
          nodes {
            ...WorkspaceUserItem
          }
        }
        linkedCards {
          ... on GoalCard {
            id
            title
            severity
            featured
            endDate
            startDate
            goalCardStatus
          }
          ... on EventCard {
            id
            title
            endDate
            startDate
            eventCardStatus
          }
        }
      }
    }
  }
  ${workspaceViewerCanFragment}
  ${goalCardItemFragment}
  ${workspaceUserItemFragment}
`;

export const TimelineWithGoalCardsQuery = gql`
  query TimelineWithGoalCardsQuery($workspaceIds: [ID!], $status: [GoalCardStatusEnum!], $parentDomainIds: [ID!]) {
    goalCards(workspaceIds: $workspaceIds, status: $status, parentDomainIds: $parentDomainIds) {
      nodes {
        id
        title
        severity
        createdAt
        archivedAt
        completedAt
        startDate
        endDate
      }
    }
  }
`;

/**
 * Adds the attachmentCount to the goalCards query
 * since this is an expensive property, it needs to stay separate, and not part of the fragment
 */
export const GoalCardsOverviewQuery = gql`
  query GoalCardsOverviewQuery(
    $workspaceIds: [ID!]
    $status: [GoalCardStatusEnum!]
    $severity: [GoalCardSeverityEnum!]
    $subDomainIds: [ID!]
  ) {
    goalCards(workspaceIds: $workspaceIds, status: $status, severity: $severity, subDomainIds: $subDomainIds) {
      nodes {
        ...GoalCardItemFragment

        attachmentCount

        users {
          nodes {
            ...WorkspaceUserItem
          }
        }
      }
    }
  }
  ${workspaceUserItemFragment}
  ${goalCardItemFragment}
`;

export const GoalCardsQuery = gql`
  query GoalCardsQuery(
    $workspaceIds: [ID!]
    $status: [GoalCardStatusEnum!]
    $severity: [GoalCardSeverityEnum!]
    $featured: Boolean
    $subDomainIds: [ID!]
  ) {
    goalCards(
      workspaceIds: $workspaceIds
      status: $status
      severity: $severity
      featured: $featured
      subDomainIds: $subDomainIds
    ) {
      nodes {
        ...GoalCardItemFragment

        users {
          nodes {
            ...WorkspaceUserItem
          }
        }
      }
    }
  }
  ${workspaceUserItemFragment}
  ${goalCardItemFragment}
`;

export const GoalCardsForExportQuery = gql`
  query GoalCardsForExportQuery(
    $workspaceIds: [ID!]
    $status: [GoalCardStatusEnum!]
    $severity: [GoalCardSeverityEnum!]
    $subDomainIds: [ID!]
  ) {
    goalCards(workspaceIds: $workspaceIds, status: $status, severity: $severity, subDomainIds: $subDomainIds) {
      nodes {
        ...GoalCardItemForExportFragment

        users {
          nodes {
            ...WorkspaceUserItem
          }
        }
      }
    }
  }
  ${workspaceUserItemFragment}
  ${goalCardItemForExportFragment}
`;

export const GoalCardWithLinkedCardsQuery = gql`
  query GoalCardWithLinkedCardsQuery($id: ID!) {
    node(id: $id) {
      ... on GoalCard {
        id

        linkedCards {
          ... on GoalCard {
            id
            createdAt
            title
            severity
            endDate
            startDate
          }
          ... on EventCard {
            id
            createdAt
            title
            endDate
            startDate
          }
        }
      }
    }
  }
`;
